import React, { useCallback, useEffect, useRef, useState } from "react";
import { liteClient } from "algoliasearch/lite";
import { GuidanceUser, RecruitItem } from "../../index";
import GuidanceFilter, { FilterState, defaultFilterState, filterChips } from "./filter";
import Styles from "./styles.module.css";
import { minLikesOf } from "../../config";
import usePageBackground from "../../usePageBackground";
import { BUSINESS_TAG_COLORS, PREFECTURES, SALARY_MIN, prefImage } from "./options";

// 学生アプリのガイダンス検索(lib/5.account/1.items/guidance/2.search.dart)と同じ画面・同じ検索条件。
const client = liteClient(
  "U3XLK29I0Z",
  "77c53159c2dc40b09016287dd5f57ea8"
);

// アプリと同じインデックス・件数
const INDEX_NAME = "searchIndex_baseRanking_desc";
const PAGE_SIZE = 20;

const PREF_NAMES = PREFECTURES.map((p) => p.name);

type Props = {
  userInfo: GuidanceUser;
  likes: RecruitItem[];
  setLikes: React.Dispatch<React.SetStateAction<RecruitItem[]>>;
  rankCount: number;
  onBack: () => void;
  onNext: () => void;
};

type Hit = {
  objectID: string;
  name: string;
  title?: string;
  image?: string;
  storeImage?: string;
  companyId?: string;
  recruitStatus?: boolean;
  business?: string[];
  addressPref?: string;
  storeTags?: string[];
};

const storageUrl = (path: string) =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/${path}?alt=media&token=`;

// 求人ページを公開している会社は求人ページの写真、していない会社は求人票の店舗写真(アプリと同じ)
const imageUrlOf = (hit: Hit) =>
  hit.recruitStatus
    ? storageUrl(`styles%2Fresize_images%2F${hit.image}_1080x1080`)
    : storageUrl(`jobPostingsStore%2Fresize_images%2F${hit.storeImage}_1080x1080`);

const companyIdOf = (hit: Hit) =>
  hit.companyId || (hit.objectID.length > 5 ? hit.objectID.slice(0, -5) : hit.objectID);

const logoUrlOf = (hit: Hit) => storageUrl(`companies_logo%2Fresize_images%2F${companyIdOf(hit)}_200x200`);

// リスト表示の都道府県アイコン。本社の都道府県、無ければ店舗の都道府県(アプリと同じ)
const prefOf = (hit: Hit) =>
  (hit.addressPref && PREF_NAMES.includes(hit.addressPref) ? hit.addressPref : "") ||
  (hit.storeTags ?? []).find((t) => PREF_NAMES.includes(t)) ||
  "";

const shuffle = <T,>(list: T[]) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const noImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.src = "/noImage.png";
};

export default function GuidanceSearch({ userInfo, likes, setLikes, rankCount, onBack, onNext }: Props) {

  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState(false);
  const [ recruits, setRecruits ] = useState<Hit[]>([]);
  const [ page, setPage ] = useState(0);
  const [ hasMore, setHasMore ] = useState(false);
  const [ loadingMore, setLoadingMore ] = useState(false);
  const [ filter, setFilter ] = useState<FilterState>(defaultFilterState);
  const [ filterOpen, setFilterOpen ] = useState(false);
  usePageBackground(filterOpen ? "#F5F5F5" : "#FAFAFA");
  // 絞り込みは一覧に重ねず、ページごと切り替える(重ねると iPhone の Safari で下のアドレスバーの裏に灰色の帯が残るため)。
  // 開く前のスクロール位置を覚えておき、戻ったら同じ位置に戻す。
  const listScrollY = useRef(0);
  const openFilter = () => {
    listScrollY.current = window.scrollY;
    setFilterOpen(true);
  };
  const closeFilter = () => setFilterOpen(false);
  useEffect(() => {
    window.scrollTo(0, filterOpen ? 0 : listScrollY.current);
  }, [filterOpen]);
  const [ listView, setListView ] = useState(false);
  // 「あと◯個！」の案内。いいねを押すたびに3秒出す。「次に進む」を押して足りないときは補足文を差し替える(アプリと同じ)
  const [ hint, setHint ] = useState<{ count: number; sub?: string } | null>(null);
  const hintTimer = useRef<number | undefined>(undefined);
  const sentinel = useRef<HTMLDivElement>(null);

  const likedIds = likes.map((l) => l.recruitId);
  // 志望群の学校は6個、順位付けの学校は順位の数だけ
  const minLikes = minLikesOf(rankCount);
  const canNext = likedIds.length >= minLikes;
  const chips = filterChips(filter);

  // アプリの fetchRecruitsFromAlgolia と同じ組み立て
  const buildFilters = useCallback((): string => {
    const parts: string[] = [];
    if (filter.storeType) parts.push(`storeType:"${filter.storeType}"`);
    const pentagon = [...filter.treatment, ...filter.style];
    if (pentagon.length > 0) parts.push(`(${pentagon.map((t) => `pentagonHash:"${t}"`).join(" OR ")})`);
    if (filter.prefecture.length > 0) parts.push(`(${filter.prefecture.map((p) => `storeTags:"${p}"`).join(" OR ")})`);
    if (filter.business.length > 0) parts.push(filter.business.map((t) => `business:"${t}"`).join(" AND "));
    if (filter.benefit.length > 0) parts.push(filter.benefit.map((t) => `hashTagsBenefit:"${t}"`).join(" AND "));
    if (filter.education.length > 0) parts.push(filter.education.map((t) => `hashTagsEducation:"${t}"`).join(" AND "));
    if (filter.workStyle.length > 0) parts.push(filter.workStyle.map((t) => `hashTagsWorkStyle:"${t}"`).join(" AND "));
    if (filter.salary !== SALARY_MIN) parts.push(`salaryTags:"${filter.salary}"`);
    parts.push(`guidance:"${userInfo.school}"`);
    return parts.join(" AND ");
  }, [filter, userInfo.school]);

  const fetchPage = useCallback(async (pageIndex: number) => {
    const result = await client.search({
      requests: [{
        indexName: INDEX_NAME,
        query: filter.freeWord,
        hitsPerPage: PAGE_SIZE,
        page: pageIndex,
        filters: buildFilters(),
      }],
    });
    const hits = ((result.results?.[0] ?? {}) as { hits?: Hit[] }).hits ?? [];
    // アプリと同じく、読み込んだ分ごとに並びをシャッフルする
    return shuffle(hits);
  }, [buildFilters, filter.freeWord]);

  // 条件が変わったら最初から
  useEffect(() => {
    if (!userInfo.school) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchPage(0)
      .then((hits) => {
        if (cancelled) return;
        setRecruits(hits);
        setPage(0);
        setHasMore(hits.length === PAGE_SIZE);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) { setRecruits([]); setError(true); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchPage, userInfo.school]);

  // 下までスクロールしたら次の20件
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loading) return;
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting || loadingMore) return;
      setLoadingMore(true);
      try {
        const hits = await fetchPage(page + 1);
        setRecruits((prev) => {
          const ids = new Set(prev.map((r) => r.objectID));
          return [...prev, ...hits.filter((h) => !ids.has(h.objectID))];
        });
        setPage(page + 1);
        setHasMore(hits.length === PAGE_SIZE);
      } catch (e) {
        console.error(e);
        setHasMore(false);
      }
      setLoadingMore(false);
    }, { rootMargin: "400px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  useEffect(() => () => window.clearTimeout(hintTimer.current), []);

  function showLikeHint(count: number) {
    window.clearTimeout(hintTimer.current);
    setHint({ count });
    hintTimer.current = window.setTimeout(() => setHint(null), 3000);
  }

  function toggleLike(hit: Hit) {
    const recruitId = hit.objectID;
    if (!likedIds.includes(recruitId)) showLikeHint(likedIds.length + 1);
    setLikes((prev) => {
      if (prev.some((l) => l.recruitId === recruitId)) {
        return prev.filter((l) => l.recruitId !== recruitId);
      }
      return [
        ...prev,
        { recruitId, companyId: companyIdOf(hit), name: hit.name, image: hit.image, storeImage: hit.storeImage },
      ];
    });
  }

  const recruitUrl = (hit: Hit) => `https://memorii-works.com/recruit/id=${hit.objectID}&job`;

  return (
    <div className={`guidance-page ${Styles.page}`}>
      {/* 絞り込み中は一覧を隠すだけにして、読み込んだ求人やスクロール位置はそのまま残す */}
      <div className={Styles.container} style={filterOpen ? { display: "none" } : undefined}>

        {/* 上に固定する部分(アプリと同じく、一覧だけがスクロールする) */}
        <div className={Styles.header}>

        {/* × と学校名 */}
        <div className={Styles.appbar}>
          <button onClick={onBack} className={Styles.appbar_close} aria-label="戻る">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
          <div className={Styles.appbar_title}>{userInfo.school}</div>
        </div>

        {/* 条件チップ ＋ フィルター */}
        <div className={Styles.chip_bar}>
          <div className={Styles.chip_scroll}>
            {chips.map((label, i) => (
              <button key={`${label}-${i}`} className={Styles.cond_chip} onClick={openFilter}>{label}</button>
            ))}
            <button className={Styles.cond_chip} onClick={openFilter}>
              <span className={Styles.cond_chip_plus}>＋</span>検索条件を追加
            </button>
          </div>
          <button className={Styles.filter_btn} onClick={openFilter}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1B1B1B" strokeWidth="2.2" strokeLinecap="round"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg>
            フィルター
          </button>
        </div>

        {/* 進み具合 ＋「次に進む」 */}
        <div className={Styles.progress_bar}>
          <div className={`${Styles.progress_text} ${canNext ? Styles.progress_text_done : ""}`}>
            {canNext
              ? `${minLikes}個以上いいねしました（現在 ${likedIds.length}個）`
              : `${minLikes}個以上いいねしましょう！（現在 ${likedIds.length}個）`}
          </div>
          <button
            className={`${Styles.next_btn} ${canNext ? "" : Styles.next_btn_disabled}`}
            onClick={() => {
              if (canNext) { onNext(); return; }
              window.clearTimeout(hintTimer.current);
              setHint({ count: likedIds.length, sub: `${minLikes}個以上いいねすると、次のページ（志望順位づけ）に進めます。` });
            }}
          >
            次に進む
          </button>
        </div>
        </div>

        <div className={Styles.body}>
          {/* カード表示 / リスト表示 */}
          <div className={Styles.view_toggle_row}>
            <button
              className={Styles.view_toggle}
              onClick={() => setListView((v) => !v)}
              aria-label={listView ? "カード表示にする" : "リスト表示にする"}
            >
              {listView ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1B1B1B"><rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="3" width="8" height="8" rx="2" /><rect x="3" y="13" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" /></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1B1B1B"><rect x="3" y="4" width="4" height="4" rx="1" /><rect x="9" y="4" width="12" height="4" rx="1" /><rect x="3" y="10" width="4" height="4" rx="1" /><rect x="9" y="10" width="12" height="4" rx="1" /><rect x="3" y="16" width="4" height="4" rx="1" /><rect x="9" y="16" width="12" height="4" rx="1" /></svg>
              )}
            </button>
          </div>

          {loading && <div className={Styles.message}>読み込み中...</div>}

          {!loading && error && (
            <div className={Styles.message}>検索に失敗しました。時間をおいて再度お試しください。</div>
          )}

          {!loading && !error && recruits.length === 0 && (
            <div className={Styles.message}>該当するサロンがありません。</div>
          )}

          {!loading && recruits.length > 0 && (
            <div className={listView ? Styles.list : Styles.cards}>
              {recruits.map((hit) => {
                const liked = likedIds.includes(hit.objectID);
                const heart = (
                  <button
                    data-testid="guidance-heart-btn"
                    className={listView ? Styles.row_heart : Styles.card_heart}
                    onClick={(e) => { e.preventDefault(); toggleLike(hit); }}
                    aria-label={liked ? "いいねを外す" : "いいね"}
                    aria-pressed={liked}
                  >
                    <HeartIcon liked={liked} outline={listView ? "#9B9B9B" : "#fff"} size={listView ? 26 : 35} />
                  </button>
                );

                if (listView) {
                  // リスト表示: 企業ロゴ | 企業名 / 業種タグ | 都道府県アイコン(Web は♡も付ける)
                  const pref = prefOf(hit);
                  const business = hit.business ?? [];
                  return (
                    <a
                      key={hit.objectID}
                      data-testid="guidance-salon-item"
                      data-recruit-id={hit.objectID}
                      className={Styles.row}
                      href={recruitUrl(hit)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={logoUrlOf(hit)} alt="" onError={noImage} className={Styles.row_logo} />
                      <div className={Styles.row_main}>
                        <div className={Styles.row_name}>{hit.name}</div>
                        {business.length > 0 && (
                          <div className={Styles.row_tags}>
                            {business.slice(0, 2).map((tag) => <BusinessTag key={tag} label={tag} />)}
                            {business.length > 2 && <BusinessTag label={`+${business.length - 2}`} />}
                          </div>
                        )}
                      </div>
                      {pref && <img src={prefImage(pref)} alt={pref} title={pref} className={Styles.row_logo} />}
                      {heart}
                    </a>
                  );
                }

                // カード表示: 正方形の写真 + ♡ / ロゴ・サロン名・求人票マーク / タイトル
                return (
                  <a
                    key={hit.objectID}
                    data-testid="guidance-salon-item"
                    data-recruit-id={hit.objectID}
                    className={Styles.card}
                    href={recruitUrl(hit)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className={Styles.card_image_wrap}>
                      <img src={imageUrlOf(hit)} alt={hit.name} onError={noImage} className={Styles.card_image} />
                      {heart}
                    </div>
                    <div className={Styles.card_meta}>
                      <img src={logoUrlOf(hit)} alt="" onError={noImage} className={Styles.card_logo} />
                      <div className={Styles.card_name}>{hit.name}</div>
                      {hit.recruitStatus && (
                        <span className={Styles.job_mark} title="求人票あり">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3695D9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></svg>
                        </span>
                      )}
                    </div>
                    {hit.title && <div className={Styles.card_title}>{hit.title}</div>}
                  </a>
                );
              })}
            </div>
          )}

          <div ref={sentinel} />
          {loadingMore && <div className={Styles.message}>読み込み中...</div>}
        </div>
      </div>

      <GuidanceFilter
        open={filterOpen}
        filter={filter}
        onClose={closeFilter}
        onSave={(next) => { setFilter(next); listScrollY.current = 0; setFilterOpen(false); }}
      />

      {hint && (
        <LikeHint
          count={hint.count}
          goal={minLikes}
          sub={hint.sub}
          onClose={() => { window.clearTimeout(hintTimer.current); setHint(null); }}
        />
      )}
    </div>
  );
}

// いいねの数の案内(アプリの _LikeHintDialog と同じ文言)
function LikeHint({ count, goal, sub, onClose }: { count: number; goal: number; sub?: string; onClose: () => void }) {
  const c = Math.min(Math.max(count, 0), goal);
  const left = goal - count;
  const done = count >= goal;
  const justReached = done && left === 0;
  const title = done
    ? (left === 0 ? `サロンが${goal}個そろいました！` : `${count}個いいねしています`)
    : `${goal}個まで あと${left}個！`;
  const text = sub ?? (done
    ? (left === 0 ? "上部の「次に進む」ボタンから\n志望順位をつけていきましょう。" : `このまま${goal}個以上いいねしてもOKです`)
    : `気になるサロンに ♡ を押して\n${goal}個以上えらびましょう`);

  return (
    <div className={Styles.hint_backdrop} onClick={onClose}>
      <div className={Styles.hint_box} onClick={onClose}>
        <div className={Styles.hint_icon}>
          {done ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E44E70" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5 9-10" /></svg>
          ) : (
            <HeartIcon liked outline="#E44E70" size={28} />
          )}
        </div>
        <div className={Styles.hint_title}>{title}</div>
        <div className={Styles.hint_bars}>
          {Array.from({ length: goal }, (_, i) => (
            <span key={i} className={Styles.hint_bar} style={{ width: goal > 6 ? 20 : 26, background: i < c ? "#E44E70" : "#EEE1E7" }} />
          ))}
        </div>
        <div className={Styles.hint_ratio}>{c} / {goal}</div>
        <div className={Styles.hint_sub}>{text}</div>
        {justReached && (
          <div className={Styles.hint_note}>※{goal + 1}つ以上いいねを押して、次の画面で順位をつけても問題ございません。</div>
        )}
        <button className={Styles.hint_btn} onClick={onClose}>閉じる</button>
      </div>
    </div>
  );
}

function BusinessTag({ label }: { label: string }) {
  const color = BUSINESS_TAG_COLORS[label] ?? "#6B6B6B";
  return (
    <span className={Styles.business_tag} style={{ color, borderColor: `${color}59`, background: `${color}12` }}>
      {label}
    </span>
  );
}

function HeartIcon({ liked, outline, size }: { liked: boolean; outline: string; size: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill={liked ? "#E44E70" : "none"}
      stroke={liked ? "#E44E70" : outline}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
